import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Assignment as AssignmentIcon,
  OpenInNew as OpenIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { get, authGet, authPut, authPost } from '../../services/apiService';
import { isAuthenticated } from '../../services/authService';

const getSeverityColor = (severity) => {
  switch (severity) {
    case 'Critical': return 'error';
    case 'High': return 'warning';
    case 'Medium': return 'info';
    case 'Low': return 'success';
    default: return 'default';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
};

const getClosedDateColor = (closedDate, issueStatus) => {
  // Only apply color coding for calculated dates (non-resolved issues)
  if (!closedDate || issueStatus === 'Resolved') {
    return 'inherit';
  }
  
  const today = new Date();
  const targetDate = new Date(closedDate);
  const daysUntilClosure = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
  
  if (daysUntilClosure < 5) {
    return '#f44336'; // Red
  } else if (daysUntilClosure <= 20) {
    return '#ff9800'; // Amber
  } else {
    return '#4caf50'; // Green
  }
};

const SnykIssuesTable = ({ onDataChange, filters: parentFilters, collection = '' }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [editMode, setEditMode] = useState(false);
  
  // Additional filters for the table (not in parent filters)
  const [tableFilters, setTableFilters] = useState({
    severity: '',
    team: '',
    projectTarget: '',
    libraryVersion: '',
    search: '',
    dueDate: null,
    introducedFrom: null,
    introducedTo: null,
    commonLibraryFixes: ''
  });

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await get('/api/snyk/filters');
      console.log('Fetched filter options:', response);
      setFilterOptions(response || {});
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  // Combine parent filters with table filters
  // Use table filter severity if provided, otherwise fall back to parent filter
  const combinedFilters = {
    severity: tableFilters.severity || parentFilters?.severity || '',
    team: tableFilters.team || '',
    status: parentFilters?.state === 'Open' ? 'unresolved' : parentFilters?.state || 'unresolved',
    dueDateBy: tableFilters.dueDate ? dayjs(tableFilters.dueDate).format('YYYY-MM-DD') : (parentFilters?.dueDateBy || ''),
    introducedFrom: tableFilters.introducedFrom ? dayjs(tableFilters.introducedFrom).format('YYYY-MM-DD') : (parentFilters?.introducedFrom || ''),
    introducedTo: tableFilters.introducedTo ? dayjs(tableFilters.introducedTo).format('YYYY-MM-DD') : (parentFilters?.introducedTo || ''),
    projectTarget: tableFilters.projectTarget || '',
    libraryVersion: tableFilters.libraryVersion || '',
    search: tableFilters.search || '',
    showCurrentDue: parentFilters?.outOfSLA || false,
    commonLibraryFixes: tableFilters.commonLibraryFixes,
    projectCollection: collection
  };

  // Fetch issues
  const fetchIssues = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...Object.fromEntries(
          Object.entries(combinedFilters).map(([key, value]) => [
            key,
            typeof value === 'boolean' ? value.toString() : value || ''
          ])
        )
      });

      console.log('Fetching issues with params:', params.toString());
      
      // Don't use caching for now
      const response = await get(`/api/snyk/issues?${params}`);
      
      if (response && response.issues) {
        setIssues(response.issues);
        setTotalCount(response.pagination?.totalCount || 0);
        console.log('Successfully fetched issues:', response.issues.length);
      } else {
        console.error('Invalid response format:', response);
        setIssues([]);
        setTotalCount(0);
        setError('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error fetching SNYK issues:', err);
      setIssues([]);
      setTotalCount(0);
      setError(err.message || 'Failed to fetch issues');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Use a ref to track previous filter values to prevent duplicate API calls
  const prevFiltersRef = React.useRef({
    page,
    rowsPerPage,
    combinedFilters: JSON.stringify(combinedFilters),
    parentFilters: JSON.stringify(parentFilters)
  });

  useEffect(() => {
    // Convert current filters to comparable format
    const currentFilters = {
      page,
      rowsPerPage,
      combinedFilters: JSON.stringify(combinedFilters),
      parentFilters: JSON.stringify(parentFilters)
    };
    
    // Compare with previous filters
    const prevFilters = prevFiltersRef.current;
    const hasChanged = 
      prevFilters.page !== currentFilters.page ||
      prevFilters.rowsPerPage !== currentFilters.rowsPerPage ||
      prevFilters.combinedFilters !== currentFilters.combinedFilters ||
      prevFilters.parentFilters !== currentFilters.parentFilters;
    
    // Only fetch if filters have changed
    if (hasChanged) {
      fetchIssues();
      // Update the ref with current values
      prevFiltersRef.current = currentFilters;
    }
  }, [page, rowsPerPage, combinedFilters, parentFilters]);

  const handleTableFilterChange = (field, value) => {
    setTableFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filtering
  };

  const clearTableFilters = () => {
    setTableFilters({
      severity: '',
      team: '',
      projectTarget: '',
      libraryVersion: '',
      search: '',
      dueDate: null,
      introducedFrom: null,
      introducedTo: null,
      commonLibraryFixes: ''
    });
    setPage(0);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIssues(issues.map(issue => issue._id));
    } else {
      setSelectedIssues([]);
    }
  };

  const handleSelectIssue = (issueId) => {
    setSelectedIssues(prev => 
      prev.includes(issueId) 
        ? prev.filter(id => id !== issueId)
        : [...prev, issueId]
    );
  };

  const handleTeamAssignment = async (issueId, team) => {
    if (!isAuthenticated()) {
      console.warn('User must be authenticated to assign teams');
      return;
    }
    
    try {
      console.log(`Assigning team ${team} to issue ${issueId}`);
      
      // Invalidate cache when updating team assignment
      await authPut(`/api/snyk/issues/${issueId}/team`, { team }, {
        invalidateCache: '/api/snyk/issues'
      });
      
      // Update the local state to show the change immediately
      setIssues(prevIssues => 
        prevIssues.map(issue => 
          issue._id === issueId ? { ...issue, team } : issue
        )
      );
      
      // Refresh data from server
      fetchIssues();
      
      // Notify parent component
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error('Error updating team assignment:', err);
    }
  };

  const handleBulkAssign = async (team) => {
    if (!isAuthenticated()) {
      console.warn('User must be authenticated to bulk assign teams');
      return;
    }
    
    try {
      // Invalidate cache when bulk assigning teams
      await authPost('/api/snyk/issues/bulk-assign', {
        issueIds: selectedIssues,
        team
      }, {
        invalidateCache: '/api/snyk/issues'
      });
      setSelectedIssues([]);
      setBulkAssignOpen(false);
      fetchIssues(); // Refresh data
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error('Error bulk assigning teams:', err);
    }
  };

  // Initial data fetch on component mount
  useEffect(() => {
    fetchIssues();
  }, []);

  // Export to Excel handler
  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters from current filters
      const params = new URLSearchParams({
        ...Object.fromEntries(
          Object.entries(combinedFilters).map(([key, value]) => [
            key,
            typeof value === 'boolean' ? value.toString() : value || ''
          ])
        )
      });

      // Add sort parameters
      params.append('sortBy', 'severity');
      params.append('sortOrder', 'asc');

      console.log('Exporting issues to Excel with params:', params.toString());

      const response = await fetch(`/api/snyk/issues/export?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to export to Excel');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'snyk-issues.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Excel export completed successfully');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError(err.message || 'Failed to export to Excel');
    } finally {
      setLoading(false);
    }
  };

  if (loading && issues.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Additional Table Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={tableFilters.search}
              onChange={(e) => handleTableFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={tableFilters.severity}
                label="Severity"
                onChange={(e) => handleTableFilterChange('severity', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.severities?.map(severity => (
                  <MenuItem key={severity} value={severity}>
                    <Chip 
                      label={severity} 
                      size="small"
                      color={
                        severity === 'Critical' ? 'error' :
                        severity === 'High' ? 'warning' :
                        severity === 'Medium' ? 'info' : 'success'
                      }
                      sx={{ mr: 1 }}
                    />
                    {severity}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Team</InputLabel>
              <Select
                value={tableFilters.team}
                label="Team"
                onChange={(e) => handleTableFilterChange('team', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="unassigned"><em>Unassigned</em></MenuItem>
                {filterOptions.teams?.map(team => (
                  <MenuItem key={team} value={team}>{team}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Project Target</InputLabel>
              <Select
                value={tableFilters.projectTarget}
                label="Project Target"
                onChange={(e) => handleTableFilterChange('projectTarget', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.projectTargets?.slice(0, 50).map(target => (
                  <MenuItem key={target} value={target}>{target}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Library Version</InputLabel>
              <Select
                value={tableFilters.libraryVersion}
                label="Library Version"
                onChange={(e) => handleTableFilterChange('libraryVersion', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.libraryVersions?.slice(0, 50).map(version => (
                  <MenuItem key={version} value={version}>{version}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Filter by Due Date"
                value={tableFilters.dueDate}
                onChange={(newValue) => handleTableFilterChange('dueDate', newValue)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  },
                  actionBar: {
                    actions: ['clear', 'today']
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Introduced From"
                value={tableFilters.introducedFrom}
                onChange={(newValue) => handleTableFilterChange('introducedFrom', newValue)}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                  actionBar: { actions: ['clear', 'today'] }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Introduced To"
                value={tableFilters.introducedTo}
                onChange={(newValue) => handleTableFilterChange('introducedTo', newValue)}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                  actionBar: { actions: ['clear', 'today'] }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={tableFilters.commonLibraryFixes === true}
                  onChange={(e) => handleTableFilterChange('commonLibraryFixes', e.target.checked ? true : '')}
                  color="primary"
                />
              }
              label="Common Library Fixes"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={clearTableFilters}
              startIcon={<ClearIcon />}
              size="small"
              fullWidth
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Actions */}
      {selectedIssues.length > 0 && (
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography variant="body2">
            {selectedIssues.length} selected
          </Typography>
          {editMode && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AssignmentIcon />}
              onClick={() => setBulkAssignOpen(true)}
            >
              Assign Team
            </Button>
          )}
        </Box>
      )}

      {/* Edit Mode Toggle and Export Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportToExcel}
          disabled={loading || issues.length === 0}
        >
          Export to Excel
        </Button>
        {isAuthenticated() && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? 'View Mode' : 'Edit Mode'}
          </Button>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedIssues.length > 0 && selectedIssues.length < issues.length}
                  checked={issues.length > 0 && selectedIssues.length === issues.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Problem Title</TableCell>
              <TableCell>Repository</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Issue Type</TableCell>
              <TableCell>Package</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Introduced</TableCell>
              <TableCell>Last Introduced</TableCell>
              <TableCell>Closed Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {issues.map((issue) => (
              <TableRow key={issue._id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIssues.includes(issue._id)}
                    onChange={() => handleSelectIssue(issue._id)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={issue.issueSeverity}
                    color={getSeverityColor(issue.issueSeverity)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {issue.score}
                  </Typography>
                </TableCell>
                <TableCell>
                  {issue.issueUrl ? (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: 300,
                        color: 'primary.main',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                          textDecoration: 'none'
                        }
                      }}
                      onClick={() => window.open(issue.issueUrl, '_blank')}
                    >
                      {issue.problemTitle}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                      {issue.problemTitle}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200 }}>
                    {issue.repository || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 280, wordBreak: 'break-all', fontSize: '0.75rem', color: 'text.secondary' }} title={issue.filePath || ''}>
                    {issue.filePath || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {issue.issueType || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 240, wordBreak: 'break-all', fontSize: '0.75rem' }} title={issue.packageNameAndVersion || issue.originalPackageName || issue.packageName || ''}>
                    {issue.packageNameAndVersion || issue.originalPackageName || issue.packageName || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {editMode ? (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={issue.team || ''}
                        displayEmpty
                        onChange={(e) => handleTeamAssignment(issue._id, e.target.value)}
                        variant="outlined"
                        size="small"
                      >
                        <MenuItem value="">
                          <em>Unassigned</em>
                        </MenuItem>
                        {filterOptions.teams?.map(team => (
                          <MenuItem key={team} value={team}>{team}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    issue.team ? (
                      <Chip
                        label={issue.team}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Unassigned
                      </Typography>
                    )
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={issue.issueStatus}
                    color={issue.issueStatus === 'Resolved' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(issue.firstIntroduced)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(issue.lastIntroduced)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2"
                    sx={{
                      color: getClosedDateColor(issue.closedDate, issue.issueStatus),
                      fontWeight: issue.issueStatus !== 'Resolved' ? 'bold' : 'normal'
                    }}
                  >
                    {formatDate(issue.closedDate)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      {/* Bulk Assignment Dialog */}
      <Dialog open={bulkAssignOpen} onClose={() => setBulkAssignOpen(false)}>
        <DialogTitle>Assign Team to Selected Issues</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Assign a team to {selectedIssues.length} selected issues.
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Team</InputLabel>
            <Select
              label="Team"
              onChange={(e) => handleBulkAssign(e.target.value)}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {filterOptions.teams?.map(team => (
                <MenuItem key={team} value={team}>{team}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAssignOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SnykIssuesTable;
